/**
 * S-V Coverage — the honesty endpoint, rendered.
 *
 * 🔴 **A screener whose universe you cannot audit is a screener whose universe you do not know.**
 * That is why this is a nav entry rather than a tab on Screener: how many instruments were
 * excluded, by which rule, and how stale the oldest price in each stage is are an operational
 * responsibility with their own failure modes. It carries a `●` when a source is degraded.
 *
 * ## The hero is the share of the screenable universe at `FULL`
 *
 * Not the instrument count. 8,140 names sounds like coverage; 62% of them carrying accounts,
 * prices *and* a resolved identity is the number that says what the screener can actually reason
 * about, and the other 38% is where a missing verdict comes from.
 *
 * ## The cadence controls are the operator's lever on the bill, and the cap is hard
 *
 * The two segmented controls change the projection immediately, and when the projection crosses
 * the cap the screen says how many dossiers at the end of the month would not be written. ⚠ **The
 * names are still screened and still carry a verdict** — only the dossier pass costs money, so a
 * cap that is reached degrades the *research*, not the screen. Saying so is the difference between
 * a limit and a surprise.
 *
 * The rule the pass itself obeys is H10's shape: it **stops** at the cap rather than continuing on
 * cheaper prompts or a smaller model, because a dossier written under different conditions from
 * the ones the run pre-registered is not comparable to the ones before it.
 */

import { useState, type JSX } from 'react';

import { Num } from '../../design/components/data/Num.jsx';
import { SegmentedControl } from '../../design/components/controls/SegmentedControl.jsx';
import { Select } from '../../design/components/controls/Select.jsx';
import { useIsPhone } from '../../ui/useIsPhone';
import { CautionMark } from './glyphs';
import type { Cadence, Coverage, Source } from './model';

/**
 * A source's state as a lamp.
 *
 * Three **geometries** — filled is running, ringed is scheduled and not yet due, hollow is
 * degraded. Not three colours: `--attn` is spent once on this screen, on the sentence that says
 * what the degraded source means, where there is room to read it.
 */
function Lamp({ state }: { state: Source['state'] }): JSX.Element {
  return <span aria-hidden="true" className="jmt-stk-lamp" data-state={state.toLowerCase()} />;
}

/**
 * Runs a month at each cadence. 22 weekdays, 44 for twice daily, every second weekday is 11, 4.3
 * Mondays, and on-demand is zero *scheduled* runs — which is why `manual` projects nothing rather
 * than projecting the same as `weekly` with a different label.
 *
 * 🔴 **`remaining` used to be a hardcoded sibling of these, and its own comment gave it away:
 * *"how many of them are still ahead on the 9th"*.** It was right on the 9th of the month and
 * wrong on the other thirty days, so the remaining-spend projection — the number this panel exists
 * to produce — was wrong almost always. It is computed from the date now; see `runsAhead`.
 */
const RUNS_PER_MONTH: Readonly<Record<Cadence, number>> = {
  daily: 22,
  twice: 44,
  everyTwo: 11,
  weekly: 4.3,
  manual: 0,
};

/**
 * Scheduled runs still ahead this month, counted from `today` rather than assumed.
 *
 * ⚠ Weekdays, not trading days: the timer is `Mon..Fri`, so it fires on market holidays too. The
 * job screens and finds nothing new, which costs a run rather than producing a wrong one — and
 * calling these "trading days" in the copy would overstate what the schedule knows.
 */
export function runsAhead(cadence: Cadence, today: Date): number {
  if (cadence === 'manual') return 0;
  const year = today.getUTCFullYear();
  const month = today.getUTCMonth();
  const lastDay = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
  let weekdays = 0;
  let mondays = 0;
  for (let day = today.getUTCDate() + 1; day <= lastDay; day += 1) {
    const weekday = new Date(Date.UTC(year, month, day)).getUTCDay();
    if (weekday >= 1 && weekday <= 5) weekdays += 1;
    if (weekday === 1) mondays += 1;
  }
  if (cadence === 'daily') return weekdays;
  if (cadence === 'twice') return weekdays * 2;
  if (cadence === 'weekly') return mondays;
  return Math.floor(weekdays / 2);
}

/**
 * ⚠ **These describe the schedule the box actually runs, and only `daily` is real.**
 *
 * The live timer is `OnCalendar=Mon..Fri 06:15 UTC` with `Persistent=true`. The note said *"06:00
 * UTC on trading days"* — wrong on the minute and loose on the day, on a screen whose whole claim
 * is that you can audit what the system does. The other four are **projections**: selecting one
 * changes the arithmetic below, not the timer, which is why each says so.
 */
const CADENCE_NOTE: Readonly<Record<Cadence, string>> = {
  daily:
    'Live: runs 06:15 UTC, Monday to Friday — about 22 runs a month. The digest lands before the open.',
  twice:
    'Projection only. Two passes a day would be about 44 runs a month; the second would catch after-hours filings.',
  everyTwo:
    'Projection only. Every second weekday is about 11 runs a month, and a filing reaches the digest within two days.',
  weekly:
    'Projection only. Mondays alone is about 4 runs a month, so a filing on Tuesday waits six days.',
  manual:
    'Projection only. With no schedule nothing is screened and nothing is spent until you trigger a run.',
};

const CADENCE_OPTIONS = [
  { value: 'daily', label: 'Daily' },
  { value: 'twice', label: '2× daily' },
  { value: 'everyTwo', label: 'Every 2 days' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'manual', label: 'Manual' },
];

const CAP_OPTIONS = [
  { value: '3', label: '3' },
  { value: '5', label: '5' },
  { value: '10', label: '10' },
];

/**
 * Money in the wallet's own currency, or a dash when there is none to show.
 *
 * ⚠ **The currency is the wallet's, not a hardcoded `$`.** It used to be `` `$${n.toFixed(2)}` ``
 * against figures that were always 0, so nothing ever contradicted it.
 */
const money = (amount: number | undefined, currency: string | undefined): string =>
  amount == null ? '—' : `${amount.toFixed(2)} ${currency ?? ''}`.trim();

const clamp = (value: number): number => Math.max(0, Math.min(100, value));

export function CoverageScreen({ coverage }: { coverage: Coverage }): JSX.Element {
  // Structure, not CSS — see `useIsPhone`, and `ScorecardScreen`'s `PhonePick` for the defect a
  // horizontally-scrolling table produced on the one screen that must not hide its own numbers.
  const phone = useIsPhone();
  const [cadence, setCadence] = useState<Cadence>('daily');
  const [dossierCap, setDossierCap] = useState(5);

  const { spend } = coverage;
  const remaining = runsAhead(cadence, new Date());
  const perMonth = RUNS_PER_MONTH[cadence];

  // 🔴 **Everything below is withheld rather than zeroed when the rate is unknown**, which is the
  // whole correction. `perRun` is measured from two operator balance readings; until there are
  // two, a projection would be arithmetic on an invented price — and this is the honesty screen.
  const measuredPerRun = spend.perRun;
  const scaledPerRun =
    measuredPerRun == null || spend.runs === 0
      ? undefined
      : // The measured figure is an average over this month's actual cap; rescale it to whatever
        // cap is selected so moving the control moves the projection for a stated reason.
        (measuredPerRun * dossierCap) / Math.max(1, spend.written / spend.runs);
  const rest = scaledPerRun == null ? undefined : scaledPerRun * remaining;
  const projected =
    rest == null || spend.balance == null ? undefined : Math.max(0, spend.balance - rest);
  const over = projected != null && projected <= 0;

  const degraded = coverage.sources.some((source) => source.state === 'DEGRADED');

  return (
    <div className="jmt-screen jmt-screen--capped">
      <header>
        <p className="jmt-stk-panel-label">UNIVERSE AT FULL TIER</p>
        {/* ⚠ The `%` goes **inside** `Num` through `children`, not after it. Outside, it renders at
            body size beside a 56px figure and reads as a footnote glued to the number — which is
            what shipped for one round of review. `children` is `Num`'s documented override and
            keeps the display step, the tabular figures and the slashed zero. */}
        <p className="jmt-stk-hero__num">
          <Num value={coverage.fullPct} kind="plain" dp={0} step="display">
            {`${coverage.fullPct}%`}
          </Num>
        </p>
        <p className="jmt-stk-hero__sub">
          <Num value={coverage.atFull} kind="plain" dp={0} /> of{' '}
          <Num value={coverage.screenable} kind="plain" dp={0} /> screenable names carry accounts,
          prices and a resolved identity
        </p>
      </header>

      <section className="jmt-panel">
        <div className="jmt-figures">
          {coverage.counts.map((entry) => (
            <div key={entry.tier}>
              <p className="jmt-figure__label">{entry.tier}</p>
              <p className="jmt-figure__value">
                <Num value={entry.n} kind="plain" dp={0} step="lg" />
              </p>
            </div>
          ))}
        </div>
      </section>

      <section className="jmt-panel jmt-panel--flush">
        <h2 className="jmt-panel__title jmt-stk-panel-head__solo">Sources</h2>
        {phone ? (
          coverage.sources.map((source) => (
            <div className="jmt-stk-src" key={source.name}>
              <p className="jmt-stk-src__head">
                <Lamp state={source.state} />
                <span className="jmt-stk-src__name">{source.name}</span>
                <span className="jmt-stk-source__state">{source.state}</span>
              </p>
              {/* 🔴 **The three readings keep their labels on a phone.** The desktop table carries
                  them in a header row; drop the header and `06:02 · 12m · 0.0%` is three numbers
                  in a trench coat — and *staleness* is the one this screen exists to surface. */}
              <div className="jmt-stk-src__reads">
                <div>
                  <span className="jmt-stk-src__k">LAST</span>
                  <code>{source.lastSuccess}</code>
                </div>
                <div>
                  <span className="jmt-stk-src__k">STALE</span>
                  <span data-stale={source.state === 'DEGRADED' ? '' : undefined}>
                    {source.staleness}
                  </span>
                </div>
                <div>
                  <span className="jmt-stk-src__k">ERRORS</span>
                  <span>{source.errorRate}</span>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="jmt-stk-scroll">
            <div className="jmt-stk-table jmt-stk-table--sources">
              <div className="jmt-stk-table__head">
                <span>SOURCE</span>
                <span data-num>LAST SUCCESS</span>
                <span data-num>STALENESS</span>
                <span data-num>ERRORS</span>
              </div>
              {coverage.sources.map((source) => (
                <div className="jmt-stk-table__row" key={source.name}>
                  <span className="jmt-stk-source">
                    <Lamp state={source.state} />
                    <span className="jmt-stk-source__name">{source.name}</span>
                    <span className="jmt-stk-source__state">{source.state}</span>
                  </span>
                  <span data-num data-quiet>
                    <code>{source.lastSuccess}</code>
                  </span>
                  <span data-num data-stale={source.state === 'DEGRADED' ? '' : undefined}>
                    {source.staleness}
                  </span>
                  <span data-num>{source.errorRate}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </section>

      <div className="jmt-stk-split">
        <section className="jmt-panel">
          <h2 className="jmt-panel__title">Resolution</h2>
          <dl className="jmt-stk-pairs">
            {coverage.resolution.map((item) => (
              <div className="jmt-stk-pairs__row" key={item.label}>
                <dt>{item.label}</dt>
                <dd>{item.value}</dd>
              </div>
            ))}
          </dl>
          {degraded && coverage.degradedNote ? (
            <p className="jmt-stk-caution">
              <CautionMark />
              <span>{coverage.degradedNote}</span>
            </p>
          ) : null}
        </section>

        <section className="jmt-panel">
          <h2 className="jmt-panel__title">Exclusions by reason</h2>
          <dl className="jmt-stk-pairs jmt-stk-pairs--counts">
            {coverage.exclusions.map((item) => (
              <div className="jmt-stk-pairs__row" key={item.reason}>
                <dt>{item.reason}</dt>
                <dd>
                  <Num value={item.n} kind="plain" dp={0} />
                </dd>
              </div>
            ))}
          </dl>
        </section>
      </div>

      <section className="jmt-panel">
        <h2 className="jmt-panel__title">Evaluation cadence</h2>

        <div className="jmt-stk-split jmt-stk-cadence">
          <div>
            <p className="jmt-stk-panel-label">How often the market is screened</p>
            {/* 🔴 **A select on a phone, a segmented control above it — and this is the one place
                the two controls are not interchangeable.** `SegmentedControl` is vendored at a
                fixed `height: 28` with no wrap, so five options with labels like *Every 2 days*
                squeeze until their text wraps inside the segments, and at 320px the control
                overflowed the panel outright — the only real overflow left after the tables were
                rebuilt. Scrolling it sideways would hide options behind a gesture nothing hints
                at. The cap control below keeps its segments because `3 / 5 / 10` fits anywhere. */}
            {phone ? (
              <Select
                options={CADENCE_OPTIONS}
                value={cadence}
                onChange={(value: unknown) => setCadence(String(value) as Cadence)}
                width="100%"
              />
            ) : (
              <SegmentedControl
                options={CADENCE_OPTIONS}
                value={cadence}
                onChange={(value: string) => setCadence(value as Cadence)}
              />
            )}
            <p className="jmt-stk-cadence__note">{CADENCE_NOTE[cadence]}</p>
          </div>
          <div>
            <p className="jmt-stk-panel-label">Dossiers written per run</p>
            <SegmentedControl
              options={CAP_OPTIONS}
              value={String(dossierCap)}
              onChange={(value: string) => setDossierCap(Number(value))}
            />
            <p className="jmt-stk-cadence__note">
              Top-ranked actionable names only. Rules run on the whole universe either way — only
              the dossier pass costs money.
            </p>
          </div>
        </div>

        <div className="jmt-stk-spend">
          <div className="jmt-stk-spend__head">
            <p className="jmt-stk-panel-label">Wallet</p>
            <p className="jmt-stk-spend__line">
              {spend.balance == null
                ? 'not recorded'
                : `${money(spend.balance, spend.balanceCurrency)} left${
                    spend.balanceAsOf ? ` · read ${spend.balanceAsOf.slice(0, 10)}` : ''
                  }`}
            </p>
          </div>

          {/* 🔴 **The projection renders only when it is measured**, and says what is missing
              otherwise. The previous version drew this bar unconditionally against a `cap` that was
              hardcoded to 0, so it divided by zero on every render and every figure beside it read
              `$0.00` — which is how a panel comes to look broken while working exactly as written. */}
          {rest != null && spend.balance != null && spend.balance > 0 ? (
            <>
              <div
                className="jmt-stk-bar"
                role="img"
                aria-label={`${money(rest, spend.balanceCurrency)} projected over ${remaining} more runs, against ${money(spend.balance, spend.balanceCurrency)} remaining`}
              >
                <span
                  className="jmt-stk-bar__proj"
                  style={{ left: 0, width: `${clamp((rest / spend.balance) * 100)}%` }}
                />
                <span className="jmt-stk-bar__cap" />
              </div>
              <p className="jmt-stk-spend__legend">
                <span>
                  <span aria-hidden="true" className="jmt-stk-key jmt-stk-key--proj" />
                  {money(rest, spend.balanceCurrency)} projected over {remaining} more run
                  {remaining === 1 ? '' : 's'} this month
                </span>
                <span className="jmt-stk-spend__cap">
                  {money(projected, spend.balanceCurrency)} left at month end
                </span>
              </p>
            </>
          ) : (
            <p className="jmt-stk-cadence__note">
              {spend.balance == null
                ? 'No projection yet: the provider serves no balance endpoint, so record what the console shows with ops/stocks_record_balance.py.'
                : 'No projection yet: a rate needs two readings. Record another after some runs have gone through, and the price is measured from your own wallet rather than a price list.'}
            </p>
          )}

          {over ? (
            <p className="jmt-stk-caution">
              <CautionMark />
              <span>
                At this cadence the balance runs out before month end. The dossier pass stops rather
                than degrading onto a cheaper model — the names are still screened and still carry a
                verdict, only the research stops.
              </span>
            </p>
          ) : null}

          {/* ⚠ Counters, not currency. These are facts the database holds outright, so they render
              whether or not a balance has ever been recorded — which is what makes this panel show
              something true on the very first load. */}
          <p className="jmt-stk-spend__foot">
            {spend.written} dossier{spend.written === 1 ? '' : 's'} written this month ·{' '}
            {spend.rejected} rejected · {spend.runs} run{spend.runs === 1 ? '' : 's'} ·{' '}
            {spend.tokensIn.toLocaleString()} tokens in
            {spend.cachedPct != null ? `, ${spend.cachedPct}% served from cache` : ''}
            {measuredPerRun != null
              ? ` · measured ${money(measuredPerRun, spend.balanceCurrency)} a run`
              : ''}{' '}
            · about {Math.round(perMonth)} runs a month at this cadence
          </p>
        </div>
      </section>

    </div>
  );
}
